import {
  AccordionSectionId,
  ChannelPreference,
  SuccessModalData,
  UseCaseConfig,
  UseCaseSuggestion,
  UseCaseType,
} from "./types";
import dayjs from "@punchup/utils/dayjs";

export const SECTION_META: Record<
  AccordionSectionId,
  { title: string; desc: string }
> = {
  "use-case": {
    title: "What message do you want to send?",
    desc: "Choose a message type for your audience",
  },
  audience: {
    title: "Who do you want to reach?",
    desc: "Select fans to receive your message",
  },
  "channel-preference": {
    title: "How do you want to reach them?",
    desc: "Choose between email, SMS, or both",
  },
  composition: {
    title: "What do you want to say?",
    desc: "Compose your message content",
  },
  review: {
    title: "Ready to send?",
    desc: "Review and confirm your message",
  },
};

export function formatChannelDistribution(
  preference: ChannelPreference,
  counts: { fanCount: number; emailCount: number; phoneCount: number }
): string {
  if (counts.fanCount === 0) return "";

  let emailDist = 0;
  let smsDist = 0;
  if (preference === "email_only" || preference === "email_and_sms")
    emailDist = counts.emailCount;
  if (preference === "sms_only" || preference === "email_and_sms")
    smsDist = counts.phoneCount;

  const parts: string[] = [];
  if (emailDist > 0)
    parts.push(
      `${emailDist.toLocaleString()} email${emailDist !== 1 ? "s" : ""}`
    );
  if (smsDist > 0)
    parts.push(`${smsDist.toLocaleString()} text${smsDist !== 1 ? "s" : ""}`);
  return parts.length > 0 ? ` (${parts.join(", ")})` : "";
}

export const USE_CASE_CONFIGS: Record<UseCaseType, UseCaseConfig> = {
  announce_show: {
    id: "announce_show",
    title: "Promote a show",
    description: "Promote your upcoming shows.",
    defaultChannels: ["email", "sms"],
    smsTemplate: "{ComedianName} has new shows! Check out the latest dates.",
    emailSubjectTemplate: "{ComedianName} has new shows!",
    emailBodyTemplate:
      "Great news — {ComedianName} just added new shows! Tickets are available now, so don't wait. Grab yours before they're gone!",
    smsTemplateSingleVenue:
      "{ComedianName} is coming to {City}! {Date} at {Venue}.",
    emailSubjectTemplateSingleVenue: "{ComedianName} is coming to {City}!",
    emailBodyTemplateSingleVenue:
      "Great news — {ComedianName} just added a show at {Venue} on {Date}! Tickets are available now, so don't wait. Grab yours before they're gone!",
    audienceType: "composite_near_shows",
    showOrdering: "datetime_asc",
    showFilter: { futureOnly: true },
    suggestedGroupCount: 3,
  },
  waitlist_new_show: {
    id: "waitlist_new_show",
    title: "Waitlist",
    description: "Notify waitlist about ticket availability.",
    defaultChannels: ["email", "sms"],
    defaultChannelPreference: "email_and_sms",
    smsTemplate: "Great news! More tickets available for {ComedianName}.",
    emailSubjectTemplate: "Great news! Tickets available for {ComedianName}",
    emailBodyTemplate:
      "You were on the waitlist, and now more tickets are available for {ComedianName}. Don't miss out — grab yours before they sell out again!",
    smsTemplateSingleVenue:
      "Great news! More tickets available for {ComedianName} at {Venue} on {Date}.",
    emailSubjectTemplateSingleVenue:
      "Great news! Tickets available for {ComedianName} at {Venue}",
    emailBodyTemplateSingleVenue:
      "You were on the waitlist, and now more tickets are available for {ComedianName} at {Venue} on {Date}. Don't miss out — grab yours before they sell out again!",
    audienceType: "waitlist",
    showOrdering: "waitlist_desc",
    showFilter: { hasWaitlist: true },
  },
  send_blast: {
    id: "send_blast",
    title: "Send a Blast",
    description: "Message your entire audience.",
    defaultChannels: ["email", "sms"],
    defaultChannelPreference: "email_and_sms",
    smsTemplate: "Hey! {ComedianName} here with an update.",
    emailSubjectTemplate: "A message from {ComedianName}",
    emailBodyTemplate: null,
    audienceType: "blast_audience",
    showOrdering: "default",
  },
};

export const USE_CASE_LIST: UseCaseConfig[] = [
  USE_CASE_CONFIGS.announce_show,
  USE_CASE_CONFIGS.waitlist_new_show,
  USE_CASE_CONFIGS.send_blast,
];

export const USE_CASE_DISABLED_REASONS: Partial<Record<UseCaseType, string>> = {
  announce_show: "No upcoming shows found",
  waitlist_new_show: "No waitlist fans found",
};

/** A use case is unavailable once loading settles with nothing to act on; a blast always is. */
export function getUseCaseAvailability(
  id: UseCaseType,
  suggestion: UseCaseSuggestion | null,
  isLoading?: boolean
): { disabled: boolean; reason: string } {
  return {
    disabled: !isLoading && id !== "send_blast" && suggestion === null,
    reason: USE_CASE_DISABLED_REASONS[id] ?? "No actionable items",
  };
}

export const SMS_MAX_SEGMENT_LENGTH = 160;
export {
  SMS_MIN_LENGTH,
  EMAIL_SUBJECT_MIN_LENGTH as EMAIL_MIN_SUBJECT_LENGTH,
} from "@punchup/data/models/messaging";

// --- Channel Preference helpers ---

export function channelFlagsFromPreference(pref: ChannelPreference): {
  emailEnabled: boolean;
  smsEnabled: boolean;
} {
  switch (pref) {
    case "email_only":
      return { emailEnabled: true, smsEnabled: false };
    case "sms_only":
      return { emailEnabled: false, smsEnabled: true };
    case "email_and_sms":
      return { emailEnabled: true, smsEnabled: true };
  }
}

export function defaultPreferenceForConfig(
  config: UseCaseConfig
): ChannelPreference {
  if (config.defaultChannelPreference) return config.defaultChannelPreference;

  const hasEmail = config.defaultChannels.includes("email");
  const hasSms = config.defaultChannels.includes("sms");

  if (hasEmail && hasSms) return "email_and_sms";
  if (hasSms) return "sms_only";
  return "email_only";
}

export const CHANNEL_PREFERENCE_LABELS: Record<ChannelPreference, string> = {
  email_only: "Email Only",
  sms_only: "SMS Only",
  email_and_sms: "Email & SMS",
};

export const CHANNEL_PREFERENCE_DESCRIPTIONS: Record<
  ChannelPreference,
  [string, string]
> = {
  email_only: [
    "All fans with an email address receive your message.",
    "No SMS will be sent.",
  ],
  sms_only: [
    "All fans with a phone number receive an SMS.",
    "No email will be sent.",
  ],
  email_and_sms: [
    "Email fans get an email, SMS fans get a text.",
    "Fans with both receive both.",
  ],
};

// ─── Magic numbers ───

export const EMAIL_MAX_BODY_LENGTH = 640;
export const EMAIL_MIN_BODY_LENGTH = 32;
export const EMAIL_SUBJECT_MAX_DISPLAY_LENGTH = 50;
export const QUIET_HOURS_START = 8;
export const QUIET_HOURS_END = 21;
export const RECENTLY_MESSAGED_HOURS = 48;

// ─── Shared success-modal data derivation ───

export function buildSuccessDisplayData(data: SuccessModalData) {
  const isScheduled = !!data.scheduledDate;
  const title = isScheduled ? "Message Scheduled!" : "Message Sent!";
  const config = USE_CASE_CONFIGS[data.useCaseType];

  const { totalUsersToReach, totalNumEmails, totalNumTexts } =
    data.audienceStats;
  const fanText = totalUsersToReach === 1 ? "fan" : "fans";

  const channelParts: string[] = [];
  if (data.emailEnabled && totalNumEmails > 0) {
    channelParts.push(
      `${totalNumEmails.toLocaleString()} email${totalNumEmails !== 1 ? "s" : ""}`
    );
  }
  if (data.smsEnabled && totalNumTexts > 0) {
    channelParts.push(
      `${totalNumTexts.toLocaleString()} text${totalNumTexts !== 1 ? "s" : ""}`
    );
  }
  const channelSuffix =
    channelParts.length > 0 ? ` (${channelParts.join(", ")})` : "";

  const scheduleLabel = isScheduled
    ? dayjs(data.scheduledDate).tz(dayjs.tz.guess()).format("MMM D [at] h:mm A")
    : "Sending now";

  // CSS truncate handles overflow; JS truncation kept as a fallback
  const truncatedSubject =
    data.emailSubject &&
    data.emailSubject.length > EMAIL_SUBJECT_MAX_DISPLAY_LENGTH
      ? data.emailSubject.slice(0, EMAIL_SUBJECT_MAX_DISPLAY_LENGTH) + "..."
      : data.emailSubject;

  return {
    isScheduled,
    title,
    config,
    totalUsersToReach,
    fanText,
    channelSuffix,
    scheduleLabel,
    truncatedSubject,
  };
}
